import { GLSL_CONSTS } from './common';

/**
 * Ripple height-field step (Evan Wallace's wave stencil) plus up to MAX_DROPS splats in the same pass.
 * R = height, G = velocity. Compiled with `PACKED` when float render targets are unavailable: then the two
 * values are stored as 16-bit fixed point across RG / BA of an RGBA8 target.
 */
export const RIPPLE_FRAG = /* glsl */ `#version 300 es
precision highp float;
precision highp sampler2D;
in vec2 vUv;
out vec4 outColor;
uniform sampler2D uPrev;
uniform vec2 uTexel;
uniform vec4 uDrops[24];
uniform int uDropCount;
uniform float uDamp;
${GLSL_CONSTS}
#ifdef PACKED
vec2 dec(vec4 t) { return vec2(t.r + t.g / 255.0, t.b + t.a / 255.0) * 2.0 - 1.0; }
vec4 enc(vec2 hv) {
  vec2 u = clamp(hv * 0.5 + 0.5, 0.0, 1.0);
  vec2 hi = floor(u * 255.0) / 255.0;
  vec2 lo = fract(u * 255.0);
  return vec4(hi.x, lo.x, hi.y, lo.y);
}
#else
vec2 dec(vec4 t) { return t.rg; }
vec4 enc(vec2 hv) { return vec4(hv, 0.0, 1.0); }
#endif
void main() {
  vec2 c = dec(texture(uPrev, vUv));
  float avg = 0.25 * (
    dec(texture(uPrev, vUv - vec2(uTexel.x, 0.0))).x +
    dec(texture(uPrev, vUv + vec2(uTexel.x, 0.0))).x +
    dec(texture(uPrev, vUv - vec2(0.0, uTexel.y))).x +
    dec(texture(uPrev, vUv + vec2(0.0, uTexel.y))).x);
  c.y += (avg - c.x) * 2.0;
  c.y *= uDamp;
  c.x += c.y;
  for (int i = 0; i < 24; i++) {
    if (i >= uDropCount) break;
    vec4 dr = uDrops[i];
    float t = max(0.0, 1.0 - length(dr.xy - vUv) / dr.z);
    t = 0.5 - cos(t * PI) * 0.5;
    c.x += t * dr.w;
  }
  c.x = clamp(c.x, -1.0, 1.0);
  c.y = clamp(c.y, -1.0, 1.0);
  outColor = enc(c);
}
`;

/** Helper compiled into the main pass to read a height from either storage. */
export const GLSL_RIPPLE_READ = /* glsl */ `
#ifdef RIPPLE_PACKED
float rippleH(sampler2D s, vec2 uv) { vec4 t = textureLod(s, uv, 0.0); return (t.r + t.g / 255.0) * 2.0 - 1.0; }
#else
float rippleH(sampler2D s, vec2 uv) { return textureLod(s, uv, 0.0).r; }
#endif
`;
